import { formatCodeableConcept } from '@medplum/core'
import type {
  Appointment,
  CareTeam,
  CodeableConcept,
  Communication,
  Composition,
  DocumentReference,
  ExtractResource,
  MedicationRequest,
  Patient,
  Practitioner,
  Reference,
  RelatedPerson,
  Resource,
  ResourceType,
  Task,
  TaskOutput,
} from '@medplum/fhirtypes'
import {
  differenceInCalendarDays,
  differenceInHours,
  isAfter,
  isBefore,
} from 'date-fns'
import { uniqBy } from 'lodash-es'
import {
  type CodingValue,
  codeableConcept,
  codeFromCodeableConcept,
  hasCodeableConcept,
  systemFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { SDR_TASK_OUTPUT_SYSTEM } from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import { invariant } from '#src/invariant.ts'
import type { Subscribeable } from '#src/models/subscribeable.ts'
import { TASK_CATEGORIES } from '#src/models/tasks/constants.ts'
import {
  SUPPORTED_TASK_INTENT,
  SUPPORTED_TASK_STATUS,
} from '#src/models/tasks/sdr/constants.ts'
import {
  SDR_TASK_OUTPUTS,
  SDRStatus,
  SDRTaskOutput,
  sdrStatusToMetadata,
} from '#src/models/tasks/sdr/utils.ts'
import { ExtendedTask } from '#src/models/tasks/task.ts'
import { createReference, getReferenceString } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  coerceToEnum,
  hasReference,
  isOfType,
  now,
  nullthrows,
} from '#src/utils.ts'

export type ActivityResource = Appointment | MedicationRequest | Composition

interface SDRTimeRemaining {
  text: string
  hoursRemaining: number
  // TODO: refactor className out of model layer and use a map to map color to status
  className: string
}

export class SDR extends ExtendedTask<SDRState> implements Subscribeable {
  constructor(data?: Partial<Omit<Task, 'resourceType'>>) {
    super({
      code: TASK_CATEGORIES.SDR,
      businessStatus: codeableConcept(makeStatusCoding(SDRStatus.REQUESTED)),
      intent: 'proposal',
      status: 'received',
      ...data,
    })
  }
  /** Extension properties */
  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-category',
    kind: 'valueString',
  })
  category: string | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-review-type',
    kind: 'valueString',
  })
  reviewType: string | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-receipt-method',
    kind: 'valueString',
  })
  receiptMethod: string | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-requested-fulfillment-date',
    kind: 'valueString',
  })
  requestedFulfillmentDate: string | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-notification-date',
    kind: 'valueDateTime',
  })
  notificationDate: string | undefined

  // #region IDT Review
  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-review/reviewDate',
    kind: 'valueDateTime',
  })
  reviewDate: string | undefined

  // TODO: make this an enum?
  static DEFAULT_INFORMED_METHOD = 'Morning IDT Meeting'

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-review/informedMethod',
    kind: 'valueString',
  })
  reviewInformedMethod: string | undefined = SDR.DEFAULT_INFORMED_METHOD

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-review/involvedIDTMember',
    kind: 'valueReference',
  })
  reviewInvolvedIDTMember: Reference<Practitioner | CareTeam> | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-review/notes',
    kind: 'valueString',
  })
  reviewNotes: string | undefined
  // #endregion

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-assessment',
    kind: 'valueString',
  })
  assessment: string | undefined

  // #region IDT Disposition
  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-disposition/dispositionDate',
    kind: 'valueDateTime',
  })
  dispositionDate: string | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-disposition/dispositionOutcome',
    kind: 'valueString',
  })
  dispositionOutcome: SDRStatus | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-disposition/involvedIDTMember',
    kind: 'valueReference',
  })
  dispositionInvolvedIDTMember: Reference | undefined

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/sdr-idt-disposition/rationale',
    kind: 'valueString',
  })
  dispositionRationale: string | undefined
  // #endregion

  get timeRemaining(): SDRTimeRemaining {
    invariant(
      this.resource.executionPeriod?.end,
      `${this.reference.reference} must have an execution period end date to calculate time remaining`
    )
    const _now = now()
    const dueDate = new Date(this.resource.executionPeriod.end)
    const hoursRemaining = differenceInHours(dueDate, _now)

    const notificationDate = this.notificationDate

    if (notificationDate) {
      const notifiedOnTime = !isAfter(new Date(notificationDate), dueDate)
      return {
        text: notifiedOnTime ? 'Achieved' : 'Missed',
        hoursRemaining,
        className: '',
      }
    }

    if (isBefore(dueDate, _now)) {
      // TODO should this actually send the task to appeal, per the deadlines?
      // see https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-E/part-460/subpart-G/section-460.121#p-460.121(j)(1)
      return {
        text: 'Overdue',
        hoursRemaining,
        className: 'text-red-600',
      }
    }

    const daysRemaining = differenceInCalendarDays(dueDate, _now)
    const timeText =
      daysRemaining > 1 ? `${daysRemaining}d` : `${hoursRemaining}h`
    return {
      text: `Due: ${timeText}`,
      hoursRemaining,
      className: '',
    }
  }

  private getOutputs(field: SDRTaskOutput): TaskOutput[] | undefined {
    return this.resource.output?.filter(
      (output) =>
        systemFromCodeableConcept(output.type) === SDR_TASK_OUTPUT_SYSTEM &&
        codeFromCodeableConcept(output.type) === field
    )
  }

  get idtDisposition(): {
    dispositionDate: Date
    dispositionOutcome: SDRStatus

    involvedIDTMember: Reference | undefined
    rationale: string
  } | null {
    if (
      !this.dispositionDate ||
      !this.dispositionOutcome ||
      !this.dispositionRationale
    ) {
      return null
    }

    return {
      dispositionDate: new Date(this.dispositionDate),
      dispositionOutcome:
        coerceToEnum(this.dispositionOutcome, SDRStatus) ?? SDRStatus.REQUESTED,

      involvedIDTMember: this.dispositionInvolvedIDTMember,
      rationale: this.dispositionRationale,
    }
  }

  get isInAppeal(): boolean {
    return this.status === SDRStatus.IN_APPEAL
  }

  get businessStatus(): CodeableConcept | undefined {
    return this.resource.businessStatus
  }

  get status(): SDRStatus | null {
    return getStatusFromCode(this.businessStatus)
  }

  get progressLabel(): string {
    return this.notificationDate
      ? `Complete: ${
          sdrStatusToMetadata[
            nullthrows(
              this.idtDisposition?.dispositionOutcome,
              'We should have a disposition outcome by this point'
            )
          ].label
        }`
      : 'Final Complete'
  }

  get for(): Reference<Patient> {
    invariant(
      isOfType(this.resource.for, 'Patient'),
      `${this.reference.reference} must have a Patient reference in the "for" field`
    )
    return this.resource.for
  }

  get createdAt(): Date {
    invariant(
      this.resource.authoredOn,
      `${this.reference.reference} must have an authoredOn date`
    )
    return new Date(this.resource.authoredOn)
  }

  get receiptDate(): Date | undefined {
    return this.resource.executionPeriod?.start
      ? new Date(this.resource.executionPeriod.start)
      : undefined
  }

  get decisionNotificationDate(): Date | undefined {
    return this.resource.executionPeriod?.end
      ? new Date(this.resource.executionPeriod.end)
      : undefined
  }

  set decisionNotificationDate(notificationDate: string) {
    invariant(
      this.resource.executionPeriod,
      `${this.reference.reference} must have an execution period`
    )
    this.resource.executionPeriod.end = notificationDate
  }

  override get requester(): Reference<Patient | RelatedPerson> | undefined {
    return this.resource.requester as
      | Reference<Patient | RelatedPerson>
      | undefined
  }

  get participantRequester(): Reference<Patient> | undefined {
    return isOfType(this.resource.requester, 'Patient')
      ? this.resource.requester
      : undefined
  }
  get relatedPersonRequester(): Reference<RelatedPerson> | undefined {
    return isOfType(this.resource.requester, 'RelatedPerson')
      ? this.resource.requester
      : undefined
  }

  override mapStateToTaskFields(state: SDRState): {
    businessStatus: Task['businessStatus']
    intent: Task['intent']
    status: Task['status']
  } {
    return STATE[state as SDRState]
  }

  /** Checks if a resource is an SDR instance */
  static isInstance(resource: Resource): resource is Task {
    return (
      isOfType(resource, 'Task') &&
      hasCodeableConcept(resource.code, TASK_CATEGORIES.SDR)
    )
  }

  fulfillmentActivityReferences<T extends ResourceType>(
    type: ActivityResource['resourceType']
  ): Reference<ExtractResource<T>>[] {
    return (
      this.resource.input
        ?.filter((i) => isOfType(i.valueReference, type))
        .map((i) => i.valueReference as Reference<ExtractResource<T>>) ?? []
    )
  }

  addFulfillmentActivity(activity: ActivityResource): void {
    this.resource.input ??= []
    this.resource.input.push({
      type: { text: 'Fulfillment Activity' },
      valueReference: createReference(activity),
    })
  }

  removeFulfillmentActivity(activity: ActivityResource): void {
    this.resource.input = this.resource.input?.filter(
      (i) => !hasReference(i.valueReference, createReference(activity))
    )
  }

  private getNotificationAttempts(
    type:
      | typeof SDRTaskOutput.NOTIFICATION_ATTEMPT_VERBAL
      | typeof SDRTaskOutput.NOTIFICATION_ATTEMPT_WRITTEN
  ): Reference<Communication>[] {
    const outputs = this.getOutputs(type)
    if (!outputs) {
      return []
    }
    return outputs.map((output) => {
      invariant(
        isOfType(output.valueReference, 'Communication'),
        `${this.reference.reference} notification attempt output must reference a Communication resource`
      )
      return output.valueReference
    })
  }

  get verbalNotificationAttempts(): Reference<Communication>[] {
    return this.getNotificationAttempts(
      SDRTaskOutput.NOTIFICATION_ATTEMPT_VERBAL
    )
  }
  get writtenNotificationAttempts(): Reference<Communication>[] {
    return this.getNotificationAttempts(
      SDRTaskOutput.NOTIFICATION_ATTEMPT_WRITTEN
    )
  }

  addVerbalNotificationAttempt(communication: Reference<Communication>): void {
    this.resource.output ??= []
    this.resource.output.push({
      type: SDR_TASK_OUTPUTS[SDRTaskOutput.NOTIFICATION_ATTEMPT_VERBAL],
      valueReference: communication,
    })
  }

  addWrittenNotificationAttempt(communication: Reference<Communication>): void {
    this.resource.output ??= []
    this.resource.output.push({
      type: SDR_TASK_OUTPUTS[SDRTaskOutput.NOTIFICATION_ATTEMPT_WRITTEN],
      valueReference: communication,
    })
  }

  addDocumentReference(docRef: DocumentReference): void {
    this.resource.output ??= []
    this.resource.output.push({
      type: SDR_TASK_OUTPUTS[SDRTaskOutput.DOCUMENT_REFERENCE],
      valueReference: {
        ...createReference(docRef),
        display: docRef.content?.[0]?.attachment?.title,
      },
    })
  }

  static buildSDR(
    params: Pick<
      Task,
      | 'for'
      | 'owner'
      | 'priority'
      | 'description'
      | 'executionPeriod'
      | 'requester'
    >,
    otherParams: {
      title: string
      category: string
      receiptMethod: string
      requestedFulfillmentDate: string | undefined
      reviewType: string
      explanationDocuments: string[]
      requestDocuments: string[]
    }
  ): SDR {
    const sdr = new SDR({
      for: params.for,
      owner: params.owner,
      priority: params.priority,
      description: params.description,
      executionPeriod: params.executionPeriod,
      requester: params.requester,
    })
    sdr.title = otherParams.title
    sdr.category = otherParams.category
    sdr.reviewType = otherParams.reviewType
    sdr.receiptMethod = otherParams.receiptMethod
    sdr.requestedFulfillmentDate = otherParams.requestedFulfillmentDate
    return sdr
  }

  static searchQuery(): QueryTypes {
    return {
      code: TASK_CATEGORIES.SDR,
      _sort: '-period',
      _count: '1000',
    }
  }

  static queryByParticipant(patientRef: Reference<Patient>): QueryTypes {
    return {
      ...SDR.searchQuery(),
      subject: patientRef.reference,
    }
  }

  static queryOpenByParticipant(patientRef: Reference<Patient>): QueryTypes {
    return {
      ...SDR.queryByParticipant(patientRef),
      // 'accepted' = SDR_APPROVED — a final clinical disposition, not withdrawable on disenrollment
      'status:not': [
        'accepted',
        'rejected',
        'cancelled',
        'completed',
        'entered-in-error',
      ],
    }
  }

  subscribers_?: Reference<Practitioner>[]

  get subscribers(): Reference<Practitioner>[] {
    return this.subscribers_ ?? []
  }

  set subscribers(subscribers: Reference<Practitioner>[]) {
    if (subscribers.length === 0) {
      this.subscribers_ = undefined
    } else {
      this.subscribers_ = uniqBy(subscribers, (s) => getReferenceString(s))
    }
  }

  addSubscriber(subscriber: Reference<Practitioner>): void {
    this.subscribers = [...this.subscribers, subscriber]
  }

  removeSubscriber(subscriber: Reference<Practitioner>): void {
    this.subscribers = this.subscribers.filter(
      (s) => s.reference !== subscriber.reference
    )
  }

  isSubscriber(practitioner: Reference<Practitioner>): boolean {
    return this.subscribers.some((s) => s.reference === practitioner.reference)
  }
}

// TODO: make this a real coding system
const STATUS_CODING_SYSTEM = 'SDRStatus'
function makeStatusCoding(status: SDRStatus): CodingValue {
  return {
    system: STATUS_CODING_SYSTEM,
    code: status,
    display: sdrStatusToMetadata[status].label,
  }
}

function getStatusFromCode(
  code: CodeableConcept | undefined
): SDRStatus | null {
  return coerceToEnum(
    code?.coding?.find((coding) => coding.system === STATUS_CODING_SYSTEM)
      ?.code,
    SDRStatus
  )
}

const BUSINESS_STATUS = {
  // in progress states
  [SDRStatus.REQUESTED]: codeableConcept(makeStatusCoding(SDRStatus.REQUESTED)),
  [SDRStatus.IN_REVIEW]: codeableConcept(makeStatusCoding(SDRStatus.IN_REVIEW)),

  // disposition states
  [SDRStatus.APPROVED]: codeableConcept(makeStatusCoding(SDRStatus.APPROVED)),
  [SDRStatus.DENIED]: codeableConcept(makeStatusCoding(SDRStatus.DENIED)),
  [SDRStatus.PARTIALLY_APPROVED]: codeableConcept(
    makeStatusCoding(SDRStatus.PARTIALLY_APPROVED)
  ),
  [SDRStatus.PARTIALLY_DENIED]: codeableConcept(
    makeStatusCoding(SDRStatus.PARTIALLY_DENIED)
  ),

  // other states
  [SDRStatus.WITHDRAWN]: codeableConcept(makeStatusCoding(SDRStatus.WITHDRAWN)),
  [SDRStatus.IN_APPEAL]: codeableConcept(makeStatusCoding(SDRStatus.IN_APPEAL)),
}

// see state diagram: https://www.figma.com/board/nmfefcAZI3U47FihtGfmOi/EHR-SDR-Data-Modeling?node-id=0-1&t=y9RAchSZ0lrE8YrA-0
const STATE = {
  SDR_REQUESTED: {
    businessStatus: BUSINESS_STATUS.SDR_REQUESTED,
    intent: SUPPORTED_TASK_INTENT.PROPOSAL,
    status: SUPPORTED_TASK_STATUS.RECEIVED,
  },
  SDR_IN_REVIEW: {
    businessStatus: BUSINESS_STATUS.SDR_IN_REVIEW,
    intent: SUPPORTED_TASK_INTENT.PLAN,
    status: SUPPORTED_TASK_STATUS.REQUESTED,
  },
  SDR_APPROVED: {
    businessStatus: BUSINESS_STATUS.SDR_APPROVED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.ACCEPTED,
  },
  SDR_DENIED: {
    businessStatus: BUSINESS_STATUS.SDR_DENIED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.REJECTED,
  },
  SDR_PARTIALLY_DENIED: {
    businessStatus: BUSINESS_STATUS.SDR_PARTIALLY_DENIED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.IN_PROGRESS,
  },
  SDR_PARTIALLY_APPROVED: {
    businessStatus: BUSINESS_STATUS.SDR_PARTIALLY_APPROVED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.IN_PROGRESS,
  },
  SDR_WITHDRAWN: {
    businessStatus: BUSINESS_STATUS.SDR_WITHDRAWN,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.CANCELLED,
  },
  SDR_IN_APPEAL: {
    businessStatus: BUSINESS_STATUS.SDR_IN_APPEAL,
    intent: SUPPORTED_TASK_INTENT.PLAN,
    status: SUPPORTED_TASK_STATUS.ON_HOLD,
  },
}
// enforce every business status has one and only one state
export type SDRState = keyof typeof BUSINESS_STATUS

export const SDR_STATE_SELECT_OPTIONS = Object.entries(STATE).map(
  ([key, value]) => ({
    value: key,
    label: formatCodeableConcept(value.businessStatus),
  })
)
